import {Component, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, RouterLink} from '@angular/router';
import {DictionaryStatistics} from '@twpub/core/models/dictionary-statistics';
import {MatDialog} from '@angular/material/dialog';
import {DatePipe, NgClass} from '@angular/common';
import {timeStr} from '@twpub/core/utils';
import {ConfirmDialogComponent} from '../confirm-dialog/confirm-dialog.component';
import {DictionaryService, FeatureFlagService} from '@twpub/core/services';
import {SectionsPanelComponent} from '../sections-panel/sections-panel.component';
import {LanguagesPanelComponent} from '../languages-panel/languages-panel.component';
import {MatButton} from '@angular/material/button';
import {MatIcon} from '@angular/material/icon';
import {DictionarySuggestionSettingsComponent} from '../dictionary-suggestion-settings/dictionary-suggestion-settings.component';
import {SyncStatusIndicatorComponent} from '../sync-status-indicator/sync-status-indicator.component';
import {MatCardModule} from '@angular/material/card';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {interval, Subscription} from 'rxjs';
import {NotifierService} from '../../services/notifier.service';
import {TenantService} from '@twpub/core/services/tenant.service';

@Component({
  selector: 'pub-dictionary-details',
  templateUrl: './dictionary-details.component.html',
  styleUrls: ['./dictionary-details.component.scss'],
  standalone: true,
  imports: [
    RouterLink, NgClass, SectionsPanelComponent, LanguagesPanelComponent, MatButton, MatIcon,
    DictionarySuggestionSettingsComponent, SyncStatusIndicatorComponent, MatCardModule, MatProgressSpinner
  ]
})
export class DictionaryDetailsComponent implements OnInit, OnDestroy {
  dictStat?: DictionaryStatistics;
  loading = true;
  notFound = false;
  suggestionsEnabled = false;
  private pollSubscription?: Subscription;

  constructor(private dictService: DictionaryService, private dialog: MatDialog,
              private route: ActivatedRoute, private router: Router,
              private datePipe: DatePipe, private notifier: NotifierService,
              private tenantService: TenantService,
              private featureFlagService: FeatureFlagService) {
  }

  ngOnInit(): void {
    this.loadDictionary();
    this.loadSuggestionSettingsVisibility();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private get dictId(): number {
    return Number(this.route.snapshot.paramMap.get('id'));
  }

  private loadSuggestionSettingsVisibility(): void {
    // Two gates: (1) the global TermSuggestions feature flag must be on, AND
    // (2) the current tenant must have opted in. Either being off hides the panel.
    this.featureFlagService.waitForReady(() => {
      if (!this.featureFlagService.isFeatureEnabled('TermSuggestions', false)) {
        this.suggestionsEnabled = false;
        return;
      }
      this.tenantService.getCurrentTenant().subscribe({
        next: tenant => this.suggestionsEnabled = !!tenant.suggestionsEnabled,
        error: () => this.suggestionsEnabled = false
      });
    });
  }

  private loadDictionary(): void {
    this.loading = true;
    this.dictService.getDictionaryStatistic(this.dictId).subscribe({
      next: (stat) => {
        this.loading = false;
        this.notFound = !stat;
        this.dictStat = stat;
        if (stat && this.isInProgress(stat)) {
          this.startPolling();
        } else {
          this.stopPolling();
        }
      },
      error: () => {
        this.loading = false;
        this.notFound = true;
        this.stopPolling();
      }
    });
  }

  private refreshDictionaryStatus(): void {
    this.dictService.getDictionaryStatistic(this.dictId).subscribe({
      next: (stat) => {
        this.notFound = !stat;
        this.dictStat = stat;
        if (!stat || !this.isInProgress(stat)) {
          this.stopPolling();
        }
      },
      error: () => this.stopPolling()
    });
  }

  private startPolling(): void {
    if (this.pollSubscription) {
      return;
    }
    this.pollSubscription = interval(2000).subscribe(() => this.refreshDictionaryStatus());
  }

  private stopPolling(): void {
    if (this.pollSubscription) {
      this.pollSubscription.unsubscribe();
      this.pollSubscription = undefined;
    }
  }

  isComplete(stat: DictionaryStatistics = this.dictStat!): boolean {
    const status = stat?.syncStats?.status;
    return status === 'COMPLETE' || status === 'WARNING';
  }

  isInProgress(stat: DictionaryStatistics = this.dictStat!): boolean {
    return stat?.syncStats?.status === 'IN_PROGRESS' || stat?.syncStats?.status === 'DELETING';
  }

  statusLabel(stat: DictionaryStatistics = this.dictStat!): string {
    if (stat?.deleted) {
      return 'Deleted in TermWeb';
    }
    switch (stat?.syncStats?.status) {
      case 'IDLE': return 'Not indexed';
      case 'IN_PROGRESS': return 'Synchronizing';
      case 'COMPLETE': return 'Synchronized';
      case 'WARNING': return 'Synchronized with warnings';
      case 'FAILED': return 'Synchronization failed';
      case 'DELETING': return 'Deleting index';
      default: return 'Unknown';
    }
  }

  statusClass(stat: DictionaryStatistics = this.dictStat!): string {
    if (stat?.deleted) {
      return 'status-failed';
    }
    switch (stat?.syncStats?.status) {
      case 'COMPLETE': return 'status-complete';
      case 'WARNING': return 'status-warning';
      case 'FAILED': return 'status-failed';
      case 'IN_PROGRESS':
      case 'DELETING': return 'status-progress';
      default: return 'status-idle';
    }
  }

  publishedConceptCount(stat: DictionaryStatistics = this.dictStat!): string | number {
    return this.isComplete(stat) ? stat.publishedConceptCount : '-';
  }

  lastSyncDate(stat: DictionaryStatistics = this.dictStat!): string {
    if (!this.isComplete(stat) || !stat.syncStats?.lastSyncDate) {
      return '-';
    }
    return this.datePipe.transform(stat.syncStats.lastSyncDate, 'yyyy-MM-dd HH:mm:ss') || '-';
  }

  syncDuration(stat: DictionaryStatistics = this.dictStat!): string {
    if (!this.isComplete(stat)) {
      return '-';
    }
    return timeStr(stat.syncStats.lastSyncTimeMs);
  }

  initialSyncDuration(stat: DictionaryStatistics = this.dictStat!): string | null {
    if (!this.isComplete(stat) || stat.syncStats.lastSyncTimeMs === stat.syncStats.initialSyncTimeMs) {
      return null;
    }
    return timeStr(stat.syncStats.initialSyncTimeMs);
  }

  deleteIndexPrompt(): void {
    if (!this.dictStat) {
      return;
    }
    const dictId = this.dictStat.id;
    const message: string[] = ['Are you sure you want to delete all synchronized data for "' + this.dictStat.name + '"?'];
    const confirmDialogRef = this.dialog.open(ConfirmDialogComponent, {data: {message}});

    confirmDialogRef.afterClosed().subscribe(confirmed => {
      if (confirmed) {
        this.dictService.deleteIndex(dictId).subscribe({
          next: () => {
            this.dictStat = {
              ...this.dictStat!,
              syncStats: {...this.dictStat!.syncStats, status: 'DELETING'}
            };
            this.notifier.showPopupMessage(`Deleting index for "${this.dictStat.name}"`);
            this.startPolling();
          }
        })
      }
    });
  }

  backToList(): void {
    this.router.navigate(['/admin/dictionaries']);
  }
}
